import { useEffect } from "react";

import { useTurnstile } from "@/common/hooks/useTurnstile";
import { ValueCheckChecking } from "@/features/value-check/components/ValueCheckChecking";
import { ValueCheckFailureView } from "@/features/value-check/components/ValueCheckFailureView";
import { ValueCheckLanding } from "@/features/value-check/components/ValueCheckLanding";
import { ValueCheckResultView } from "@/features/value-check/components/ValueCheckResultView";
import { useValueCheck } from "@/features/value-check/hooks/useValueCheck";
import { VALUE_CHECK_TURNSTILE_ACTION } from "@/features/value-check/value-check.static";

/**
 * WHO-6534, "What is your item worth?": landing, checking, the answer (or what went wrong). One
 * photo at a time; "Check another item" starts over.
 */
export const ValueCheckPage = () => {
  const { Component: Turnstile, getHeaders } = useTurnstile();
  const { phase, last, check, reset, openLast } = useValueCheck({
    getHeaders,
  });

  /*
   * Each step replaces the whole page, and the second upload button sits at the bottom of the
   * landing: without this the visitor stays at the footer and misses the check and its answer.
   */
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [phase.name]);

  const view = () => {
    switch (phase.name) {
      case "idle":
        return (
          <ValueCheckLanding
            onPick={check}
            lastCheck={
              last?.result.recognised && last.result.title
                ? { photoUrl: last.photoUrl, title: last.result.title }
                : null
            }
            onOpenLastCheck={openLast}
          />
        );
      case "checking":
        return <ValueCheckChecking photoUrl={phase.photoUrl} />;
      case "done":
        return (
          <ValueCheckResultView
            photoUrl={phase.photoUrl}
            result={phase.result}
            onCheckAnother={reset}
            onPick={check}
          />
        );
      case "failed":
        return (
          <ValueCheckFailureView failure={phase.failure} onTryAgain={reset} />
        );
    }
  };

  return (
    // White: brand is never a light background (Notion 09.1, WHO-6534 F19).
    <div className="bg-white">
      <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-12">
        {view()}
      </div>
      <Turnstile action={VALUE_CHECK_TURNSTILE_ACTION} />
    </div>
  );
};
